import React from "react"

import {
	Grid,
} from "@mui/material"

import {
	plural, t,
} from "@lingui/macro"

import {
	ErrorResponse, Loading,
	RestResponse,
} from "@sinossi/mates-react-library"

import {
	Action,
	AssetMatchBrandWithImage,
	BrandPriority,
	DashboardResponse,
	GeoAssetFilter,
	OrderType,
	PaginatedResponse,
	SearchFilter, SearchMatchRequest, SearchPriorityMatch,
	SessionResponse,
} from "models"
import { MatchService } from "services/match.service"

import {
	DashboardCard,
	DashboardCardsData,
	DashboardMap,
	DashboardCardWithList,
} from "."
import { AccountServices } from "services/account.services"
import UploadDocumentModal from "../asset/asset-details/asset-documents-table/upload-document-modal"
import { DashboardServices } from "services/dashboard.services"

interface DashboardBrandProps {
	dashboardData: DashboardResponse
	actions: PaginatedResponse<Action> | null
	notifications: PaginatedResponse<Action> | null

	onException: (response: RestResponse<any, ErrorResponse>) => void
	refreshActions: () => void
	sessionData: SessionResponse
}
interface DashboardBrandState {
	loading: boolean
	compatibleAssets: PaginatedResponse<AssetMatchBrandWithImage> | null
	compatibleAssetsFilter: SearchPriorityMatch
	pagination: SearchFilter

	mapMatches: AssetMatchBrandWithImage[]
	mapAssetFilter: GeoAssetFilter | null

	likes: string[]

	formRequest: SearchMatchRequest | null
	brandId: string

	defaultBounds: google.maps.LatLngBounds | null

	openUploadDocument: boolean
	reloadPriorities: boolean
	selectedPriority: BrandPriority | null
	totalPages: number
}
export class DashboardBrand extends React.Component<DashboardBrandProps, DashboardBrandState> {
	private readonly compatibleAssetsRef = React.createRef<HTMLDivElement>()

	private defaultCompatibleFilter: SearchPriorityMatch = {
		pageSize: 10,
		pageId: 0,
		orderType: null,
		orderBy: null,
		priorityId: null
	}

	constructor(props: DashboardBrandProps) {
		super(props)
		this.state = {
			loading: true,

			totalPages: 1,
			compatibleAssets: null,
			compatibleAssetsFilter: this.defaultCompatibleFilter,
			pagination: new SearchFilter(),

			mapMatches: [],
			mapAssetFilter: null,

			likes: [],

			formRequest: null,
			brandId: this.props.sessionData.brands[0].id,
			defaultBounds: null,

			openUploadDocument: false,
			reloadPriorities: false,
			selectedPriority: null
		}
	}

	componentDidMount() {
		this.getCompatibleAssets()
		this.getMatchListByArea()
	}

	private getCompatibleAssets = (): void => {
		MatchService.getMatchesByCompanyId(this.state.compatibleAssetsFilter, this.getMatchCallback, this.props.onException)
	}

	private getMatchCallback = (response: RestResponse<PaginatedResponse<AssetMatchBrandWithImage>, ErrorResponse>): void => {
		if (response.hasError()) {
			this.props.onException(response)
			return
		}

		this.setState(
			() => ({
				compatibleAssets: response.data,
			}),
			() => AccountServices.getAssetLikes(this.getAssetLikesCallback, this.props.onException)
		)
	}


	private updateLikes = (likes: string[]) => {

		this.setState(
			() => ({
				likes: likes
			}))
	}

	private getLikes = () => {
		AccountServices.getAssetLikes(this.getAssetLikesCallback, this.props.onException)
	}

	private getAssetLikesCallback = (response: any) => {
		if (response.hasError()) {
			this.props.onException(response)
			return
		}

		this.setState(() => ({
			likes: response.data,
			loading: false
		}))
	}

	private getMatchListByArea = (): void => {
		MatchService.getMatchesByCompanyIdAndArea(
			{
				...this.state.pagination,
				northEastLatitude: 0,
				northEastLongitude: 0,
				southWestLatitude: 0,
				southWestLongitude: 0,
				priorityId: null,
			},
			this.state.brandId,
			this.getMatchByAreaCallback,
			this.props.onException
		)
	}

	private getMatchByAreaCallback = (response: RestResponse<PaginatedResponse<AssetMatchBrandWithImage>, ErrorResponse>): void => {
		if (response.hasError()) {
			this.props.onException(response)
			return
		}

		if (this.state.compatibleAssetsFilter.priorityId) {
			this.setState(() => ({
				mapMatches: response.data?.items ?? [],
				totalPages: response.data!.pagination.totalPages
			}))
		}
		else {
			this.setState(() => ({
				mapMatches: response.data?.items ?? [],
				totalPages: response.data!.pagination.totalPages,
				loading: false
			}))
		}

	}

	private getFilteredMatchList = () => {
		if (this.state.formRequest) {
			MatchService.filterMatchByBrand(this.state.brandId, this.state.formRequest, this.getFilteredMatchListCallback, this.props.onException)
		}
	}

	private getFilteredMatchListCallback = (response: RestResponse<PaginatedResponse<AssetMatchBrandWithImage>, ErrorResponse>) => {
		if (response.hasError()) {
			this.props.onException(response)
			return
		}

		this.setState(() => ({
			mapMatches: response.data?.items ?? [],
			loading: false
		}))
	}

	private calculateNumberOfAssets = () => {
		return plural(
			this.state.compatibleAssets?.pagination.totalRecords ?? 0,
			{
				one: "# IMMOBILE COMPATIBILE",
				other: "# IMMOBILI COMPATIBILI",
			},
		)
	}

	private handleCompatibleAssets = () => {
		this.compatibleAssetsRef.current?.scrollIntoView({
			behavior: "smooth",
		})
	}

	private setBrandPriority = (brandPriority: BrandPriority | null, brandId: string) => {
		this.setState(() => ({
			compatibleAssetsFilter: {
				...this.state.compatibleAssetsFilter,
				priorityId: brandPriority?.id ?? null,
			},
			brandId: brandId,
			selectedPriority: brandPriority
		}), () => {
			this.getCompatibleAssets()
			this.onMapBoundsChange(this.state.defaultBounds!, brandPriority)
		})
	}

	private onMapBoundsChange = (bounds: google.maps.LatLngBounds, brandPriority: BrandPriority | null): void => {
		if (bounds !== undefined && !this.state.defaultBounds) {
			const northEast = bounds.getNorthEast();
			const southWest = bounds.getSouthWest();
			if (!this.state.formRequest) {
				this.setState(
					() => ({
						loading: true,
						mapAssetFilter: {
							pageSize: 50,
							pageId: 0,
							orderType: OrderType.ASCENDING,
							orderBy: null,
							northEastLatitude: this.roundNumber(northEast.lat(), 12),
							northEastLongitude: this.roundNumber(northEast.lng(), 12),
							southWestLatitude: this.roundNumber(southWest.lat(), 12),
							southWestLongitude: this.roundNumber(southWest.lng(), 12),
							priorityId: brandPriority?.id ?? null,
						},
						defaultBounds: this.state.defaultBounds ?? bounds,
						mapMatches: []
					}),
					this.getMatchListByArea
				)
			}
		}
		if (bounds === this.state.defaultBounds) {
			const northEast = bounds.getNorthEast();
			const southWest = bounds.getSouthWest();
			this.setState(
				() => ({
					loading: true,
					mapAssetFilter: {
						pageSize: 50,
						pageId: 0,
						orderType: OrderType.ASCENDING,
						orderBy: null,
						northEastLatitude: this.roundNumber(northEast.lat(), 12),
						northEastLongitude: this.roundNumber(northEast.lng(), 12),
						southWestLatitude: this.roundNumber(southWest.lat(), 12),
						southWestLongitude: this.roundNumber(southWest.lng(), 12),
						priorityId: brandPriority?.id ?? null,
					},
					mapMatches: []
				}),
				this.getMatchListByArea
			)
		}
	}

	private roundNumber = (num: number, dec: number) => {
		return Math.round(num * Math.pow(10, dec)) / Math.pow(10, dec);
	}
	
	private submitSearchMatch = (request: SearchMatchRequest, currentBrand: string) => {
		this.setState(() => ({
			formRequest: request,
			brandId: currentBrand,
		}), () => {
			this.searchMatch(currentBrand, request)
		})
	}

	private onBrnadChange = (brand: string) => {
		this.setState(() => ({
			brandId: brand,
			loading: true
		}), () => this.getMatchListByArea())
	}

	private searchMatch = (currentBrand: string, request: SearchMatchRequest) => {
		MatchService.searchMatchBrandDashboard(currentBrand, request, this.searchMatchCallback, this.props.onException)
	}

	private searchMatchCallback = (response: RestResponse<PaginatedResponse<AssetMatchBrandWithImage>, ErrorResponse>) => {
		if (response.hasError()) {
			this.props.onException(response)
			return
		}

		if (response) {
			this.setState(() => ({
				compatibleAssets: response.data,
			}), () => this.getFilteredMatchList())
		}
	}

	private clearForm = () => {
		this.setState(() => ({
			loading: true,
			compatibleAssetsFilter: this.defaultCompatibleFilter,
			formRequest: null,
			selectedPriority: null
		}), () => {
			if (this.state.defaultBounds) {
				this.onMapBoundsChange(this.state.defaultBounds, null)
				this.getCompatibleAssets()
			}
		})
	}

	private openUploadDocument = () => {
		this.setState(() => ({
			openUploadDocument: true
		}))
	}

	public closeUploadDocument = (data?: FormData) => {
		if (data) {
			this.setState(() => ({
				openUploadDocument: false,
				reloadPriorities: false
			}), () => (this.uploadDocuments(data)))
		}
		else {
			this.setState(() => ({
				openUploadDocument: false,
				reloadPriorities: false
			}))
		}
	}

	private uploadDocuments = (request: FormData) => {
		this.setState(() => ({
			loading: true,
		}), () => DashboardServices.uploadPrioritiesDocument(request, this.uploadDocumentCallback, this.props.onException))
	}

	private uploadDocumentCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.props.onException(response)
			return
		}

		this.setState(() => ({
			reloadPriorities: true
		}), () => {
			this.closeUploadDocument()
			this.getCompatibleAssets()
		})
	}

	private handlePagination = (_: any, value: number) => {
		if (value) {
			this.setState((prevState) => ({
				pagination: {
					...this.state.pagination,
					pageId: value,
					/*pageSize: newPageSize */
				},
				loading: true,
			}), this.getMatchListByArea)
		}
	}

	render() {
		return (
			<>
				<Loading show={this.state.loading} />
				{
					!this.state.loading &&
					<Grid container alignItems="stretch" spacing={2}>
						<Grid item xs={12} lg={6} sx={{ height: '100%' }}>
							{this.props.actions &&
								<DashboardCardWithList
									cardType={DashboardCardsData.ACTIONS}
									sessionData={this.props.sessionData}
									data={this.props.actions}
									refreshPage={this.props.refreshActions}
									showListInPopup
									brand
								/>
							}
						</Grid>
						<Grid item xs={12} lg={6} onClick={this.handleCompatibleAssets}>
							<DashboardCard
								cardType={DashboardCardsData.COMPATIBLE_ASSETS}
								value={this.calculateNumberOfAssets()}
								notifications={0}
							/>
						</Grid>
						{/*<Grid item xs={12} lg={4} sx={{ height: '100%' }}>
							<Link to="/affiliates" style={{ textDecoration: "none" }}>
								<DashboardCard
									cardType={DashboardCardsData.SEARCH_AFFILIATES}
									value="RICERCA AFFILIATO"
									notifications={0}
									customFooter={
										<Button
											type="button"
											variant="contained"
											color="secondary"
											disableElevation
											sx={{
												px: 8,
												py: 0.5,
												color: "primary.contrastText",
												fontSize: "0.5em",
												textTransform: "none",
											}}
										>
											<Trans id="dashboard.affiliates-search-button">
												Cerca
											</Trans>
										</Button>
									}
								/>
							</Link>
						</Grid>*/}
						{this.state.mapMatches &&
							<Grid item xs={12}>
								<DashboardMap
									hideTitle={true}
									matches={this.state.mapMatches}
									likes={this.state.likes}
									onMapBoundsChange={this.onMapBoundsChange}
									brandPrioritySelection={this.setBrandPriority}
									sessionData={this.props.sessionData}
									updateLikes={this.updateLikes}
									searchMatch={this.submitSearchMatch}
									clearForm={this.clearForm}
									openUploadDocument={this.openUploadDocument}
									reloadPriorities={this.state.reloadPriorities}
									selectedPriority={this.state.selectedPriority}
									currentBrand={this.state.brandId}
									onChangePagination={this.handlePagination}
									pageId={this.state.pagination.pageId}
									totalPages={this.state.totalPages}
									onBrandChange={this.onBrnadChange}
								/>
							</Grid>
						}
						{/* <Grid item xs={12} ref={this.compatibleAssetsRef}>
							<DashboardCompatibleAssets
								data={this.state.compatibleAssets}
								pagination={this.state.pagination}
								onPaginationChanged={this.onCompatibleAssetsPaginationChanged}
								onSortChanged={this.onCompatibleAssetsSortChanged}
								likes={this.state.likes}
								getLikes={this.getLikes}
							/>
						</Grid> */}
					</Grid>
				}

				{
					this.state.openUploadDocument &&
					<UploadDocumentModal open={this.state.openUploadDocument}
						onClose={this.closeUploadDocument}
						callbackUploadDocument={this.uploadDocuments}
						documentType={t({ id: "upload-document-modal.prioritiy", message: "Caricamento ricerca" })}
						content={t({ id: "upload-document-modal.priority-content", message: "Trascina o clicca per caricare una ricerca" })}
						priority
					/>
				}
			</>
		)
	}
}
